/**
 * The Email view's mailbox client, whichever provider the user's mail lives
 * with. Both proxies answer in one MIME-shaped message model (see
 * gmailMessage.ts), so this layer only picks the proxy and says what the
 * provider can do; it never reshapes messages.
 */
import { GmailService } from '../gmailService';
import { OutlookMailService } from '../outlookService';
import type { GmailMessage } from '../gmailMessage';
import { getActiveMailProvider } from './mailProvider';
import { mailCapabilities as capabilitiesFor, MAIL_PROVIDER_NAMES, type MailCapabilities, type MailProviderId } from './mailCapabilities';

export { MAIL_PROVIDER_NAMES } from './mailCapabilities';
export type { MailCapabilities } from './mailCapabilities';

export function mailCapabilities(provider: MailProviderId = getActiveMailProvider()): MailCapabilities {
  return capabilitiesFor(provider);
}

/** Something the shown mailbox cannot do from Quantis yet. */
export class MailUnsupportedError extends Error {}

function unsupported(what: string): never {
  throw new MailUnsupportedError(`${what} is not available for ${MAIL_PROVIDER_NAMES[getActiveMailProvider()]} mailboxes in Quantis yet`);
}

const outlook = () => getActiveMailProvider() === 'microsoft';

type SendPayload = Parameters<typeof GmailService.send>[0];

export const MailService = {
  provider: getActiveMailProvider,
  capabilities: () => mailCapabilities(),

  listMessages(opts: Parameters<typeof GmailService.listMessages>[0] = {}) {
    return outlook() ? OutlookMailService.listMessages(opts) : GmailService.listMessages(opts);
  },
  batchMetadata(ids: string[]) {
    return outlook() ? OutlookMailService.batchMetadata(ids) : GmailService.batchMetadata(ids);
  },
  getMessage(id: string) {
    return outlook() ? OutlookMailService.getMessage(id) : GmailService.getMessage(id);
  },
  getThread(id: string) {
    return outlook() ? OutlookMailService.getThread(id) : GmailService.getThread(id);
  },
  modify(id: string, addLabelIds: string[] = [], removeLabelIds: string[] = []): Promise<{ success: true; message?: GmailMessage }> {
    return outlook() ? OutlookMailService.modify(id, addLabelIds, removeLabelIds) : GmailService.modify(id, addLabelIds, removeLabelIds);
  },
  markRead(id: string) {
    return outlook() ? OutlookMailService.markRead(id) : GmailService.markRead(id);
  },
  markUnread(id: string) {
    return outlook() ? OutlookMailService.markUnread(id) : GmailService.markUnread(id);
  },
  archive(id: string) {
    return outlook() ? unsupported('Archiving') : GmailService.archive(id);
  },
  trash(id: string) {
    return outlook() ? unsupported('Deleting') : GmailService.trash(id);
  },
  send(payload: SendPayload) {
    return outlook() ? unsupported('Sending') : GmailService.send(payload);
  },
  reconcileNotificationReads() {
    return outlook() ? OutlookMailService.reconcileNotificationReads() : GmailService.reconcileNotificationReads();
  },
  attachmentUrl(msgId: string, attId: string, filename: string, mimeType: string) {
    return outlook()
      ? OutlookMailService.attachmentUrl(msgId, attId, filename, mimeType)
      : GmailService.attachmentUrl(msgId, attId, filename, mimeType);
  },
  fetchAttachment(msgId: string, attId: string, filename: string, mimeType: string) {
    return outlook()
      ? OutlookMailService.fetchAttachment(msgId, attId, filename, mimeType)
      : GmailService.fetchAttachment(msgId, attId, filename, mimeType);
  },
  labels() {
    return outlook() ? OutlookMailService.labels() : GmailService.labels();
  },
  label(id: string) {
    return outlook() ? OutlookMailService.label(id) : GmailService.label(id);
  },
  profile() {
    return outlook() ? OutlookMailService.profile() : GmailService.profile();
  },
  startWatch() {
    return outlook() ? OutlookMailService.startWatch() : GmailService.startWatch();
  },
};
